// What Hoy and the dialog say about the automatic preparation of a session (pure, tested by
// scripts/test-session-prep.mjs; agreed with Ale on 04-11-2026). The state comes from the server
// (shared/sessionPrep.ts › prepState); a failed query is an error with «Reintentar», never «sin preparación».

const fmt = (iso) => {
  try { return new Intl.DateTimeFormat('es', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso)); } catch { return ''; }
};

export const PREP_STATE_TEXT = {
  approved: 'Preparación aprobada',
  draft: 'Preparación lista para revisar',
  running: 'Preparando…',
  failed: 'La preparación falló',
  discarded: 'Descartaste la preparación: no se vuelve a preparar sola',
  off: 'Preparación automática apagada',
  out_of_scope: 'Este tipo de sesión está fuera del alcance de la preparación automática',
  scheduled: 'Se preparará 24 h antes',
  started: 'La sesión ya empezó: no se prepara',
  not_applicable: 'No se prepara automáticamente',
  limit: 'Se alcanzó el límite mensual de preparaciones automáticas',
  pending: 'Pendiente de preparar en la próxima pasada',
  not_found: 'No se encontró la sesión',
  uncertain: 'Resultado incierto: la IA pudo haber terminado sin que se guardara nada',
  uncertain_saving: 'La IA terminó y el resultado puede estar guardándose',
  coordination_error: 'No se pudo comprobar el estado de la preparación',
};

/** { text, tone, canOpen, canRetry }: the line next to a session in Hoy. `query.error` = the states could not be read. */
export function prepLine(p, query = {}) {
  if (query.error) return { text: 'No se pudo consultar la preparación', tone: 'error', canOpen: false, canRetryQuery: true };
  if (!p) return { text: '', tone: 'muted', canOpen: false };
  const base = PREP_STATE_TEXT[p.state] || 'Sin datos de preparación';
  const auto = p.trigger === 'auto' ? ' (automática)' : p.trigger === 'manual' ? ' (pedida por ti)' : '';
  switch (p.state) {
    case 'approved': case 'draft': return { text: `${base}${auto}`, tone: p.state === 'draft' ? 'accent' : 'ok', canOpen: true };
    case 'running': return { text: base, tone: 'muted', canOpen: true };
    case 'failed': return { text: p.autoRetry ? `${base}: reintento automático${p.retryAt ? ` ${fmt(p.retryAt)}` : ''}` : `${base}: puedes usar Reintentar`, tone: 'error', canOpen: true, canRetry: !p.autoRetry };
    case 'discarded': return { text: base, tone: 'muted', canOpen: true };
    case 'uncertain': return { text: `${base}: revísala`, tone: 'warn', canOpen: true };
    case 'uncertain_saving': return { text: `${base}: comprueba de nuevo`, tone: 'warn', canOpen: true };
    case 'coordination_error': return { text: base, tone: 'error', canOpen: true };
    case 'scheduled': return { text: p.dueAt ? `Se preparará el ${fmt(p.dueAt)} (24 h antes)` : base, tone: 'muted', canOpen: true };
    case 'limit': return { text: `${base} (${p.used} de ${p.limit})`, tone: 'warn', canOpen: true };
    case 'off': case 'out_of_scope': return { text: base, tone: 'muted', canOpen: true };
    default: return { text: base, tone: 'muted', canOpen: true };
  }
}

/** The labels of the attempts of an automatic preparation (dialog). */
export const ATTEMPT_TRIGGER = { booking: 'al reservar', scheduler: 'pasada programada', auto_retry: 'reintento automático', manual_retry: 'reintento tuyo' };
const OUTCOME = (a) => {
  if (a.ok) return 'guardado';
  const e = String(a.error || '');
  if (e === 'uncertain') return 'resultado incierto (la IA pudo haber terminado)';
  if (e === 'uncertain_saving') return 'la IA terminó; no se confirmó el guardado';
  if (/^limit_reached/.test(e)) return 'sin plaza mensual (no llamó a la IA)';
  if (/^not_eligible/.test(e)) return 'la sesión ya no correspondía (no llamó a la IA)';
  if (/^not_saved:/.test(e)) return 'no se guardó: la sesión cambió';
  if (/^coordination_/.test(e)) return 'no se pudo coordinar (no llamó a la IA)';
  if (/^interrupted/.test(e)) return 'interrumpido antes de la IA';
  return e ? `falló (${e})` : '';
};
/** One line per attempt: when, why it started, whether it called the AI and how it ended. */
export function attemptLines(attempts = []) {
  return attempts.map(a => [`Intento ${a.n || '?'}`, ATTEMPT_TRIGGER[a.trigger] || a.trigger || '', a.at ? fmt(a.at) : '', a.ai ? 'llamó a la IA' : a.ai === false ? 'sin llamar a la IA' : '', OUTCOME(a)].filter(Boolean).join(' · '));
}

/**
 * The month, three figures apart: reserved slots (from Blobs: may lag up to a minute), completed preparations and
 * attempts (with the calls to the AI, which retries can raise). A limit below what is reserved is said as such.
 */
export function countsLines(c) {
  if (!c) return [];
  const out = [];
  out.push(c.reserved === null || c.reserved === undefined ? `Plazas reservadas: ${c.reservedError || 'no disponible'}` : `Plazas reservadas este mes: ${c.reserved} de ${c.limit} (la cifra puede tardar hasta un minuto en actualizarse)`);
  out.push(`Preparaciones completadas: ${c.completed}`);
  out.push(`Intentos: ${c.attempts}, con ${c.aiCalls} ${c.aiCalls === 1 ? 'llamada' : 'llamadas'} a la IA. Los reintentos pueden consumir IA adicional.`);
  if (c.overLimit) out.push(`El límite (${c.limit}) está por debajo de lo ya reservado: no se reservan más plazas este mes. El historial se conserva y las plazas reservadas no se recuperan.`);
  return out;
}

/** What the dialog says about changes since it was prepared (computed by the server, never by the AI). */
export function changesText(changes) {
  if (!changes) return [];
  if (changes.error) return ['No se pudo comprobar si cambió el contexto de la sesión.'];
  const out = [];
  if (changes.dateChanged) out.push(`La sesión cambió de fecha${changes.previousStartsAt ? ` (antes: ${fmt(changes.previousStartsAt)})` : ''}. La preparación se conserva.`);
  if (changes.changes?.length) out.push(`Cambió ${changes.changes.map(c => c.label).join(', ')} desde que se preparó: revísala o prepárala de nuevo.`);
  return out;
}
